import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import styles from '../assets/salesWithGuardiansDashboard.module.css';
import propertyViewingsService from '../services/propertyViewingsService';
import guardiansService from '../services/guardiansService';

// Sort Icon Component matching Laravel style
const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const SalesWithGuardiansDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [guardiansInProcess, setGuardiansInProcess] = useState([]);
  const [viewings, setViewings] = useState([]);
  const [statistics, setStatistics] = useState({
    all: 0,
    upcoming: 0,
    past: 0,
    completed: 0,
    uncompleted: 0
  });
  const [selectedStatus, setSelectedStatus] = useState(999); // 999 = all

  // Table controls for Property Viewings only (matching Laravel pattern)
  const [viewingsSortConfig, setViewingsSortConfig] = useState({ key: null, direction: 'asc' });
  const [viewingsSearch, setViewingsSearch] = useState('');
  const [viewingsPerPage, setViewingsPerPage] = useState(10);
  const [viewingsCurrentPage, setViewingsCurrentPage] = useState(1);

  // Column visibility controls for Property Viewings (Laravel feature)
  const [viewingsColumnVisibility, setViewingsColumnVisibility] = useState({
    property: true,
    id: true,
    postcode: true,
    day: true,
    timeFrom: true,
    timeTo: true,
    confirmed: true,
    actions: true
  });
  const [showViewingsColumnVisibility, setShowViewingsColumnVisibility] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Check if user is loaded
      if (!user || !user.id) {
        console.warn('⚠️ [FRONTEND] User not loaded yet, waiting...');
        setLoading(false);
        return;
      }

      // Load guardians in process from database
      try {
        const guardiansResponse = await guardiansService.getGuardiansInProcess();
        console.log('✅ [FRONTEND] Guardians in process loaded:', guardiansResponse.data);
        setGuardiansInProcess(guardiansResponse.data || []);
      } catch (error) {
        console.error('❌ [FRONTEND] Failed to load guardians in process:', error);
        toast.error('Failed to load guardians in process');
        setGuardiansInProcess([]);
      }

      // Load user's property viewings from database
      // IMPORTANT: Load ALL viewings (no pagination) to match statistics counts
      try {
        const viewingsResponse = await propertyViewingsService.getUserViewings(user.id);
        console.log('✅ [FRONTEND] User viewings response:', viewingsResponse.data);
        // FIXED: Backend now returns { success: true, data: [...], total: N }, so we need data.data
        const viewingsData = viewingsResponse.data?.data || [];
        console.log('✅ [FRONTEND] Extracted viewings data:', viewingsData.length, 'viewings loaded');
        console.log('✅ [FRONTEND] Statistics show:', statistics.all, 'total viewings');
        if (viewingsData.length !== statistics.all && statistics.all > 0) {
          console.warn('⚠️ [FRONTEND] Viewings count mismatch! Loaded:', viewingsData.length, 'Expected from stats:', statistics.all);
        }
        setViewings(viewingsData);
      } catch (error) {
        console.error('❌ [FRONTEND] Failed to load user viewings:', error);
        toast.error('Failed to load user viewings');
        setViewings([]);
      }

      // Load statistics from stats endpoint (counts ALL viewings, not just paginated)
      try {
        const statsResponse = await propertyViewingsService.getUserViewingStats(user.id);
        console.log('✅ [FRONTEND] User viewing stats response:', statsResponse.data);
        const statsData = statsResponse.data?.data || statsResponse.data || {};
        setStatistics({
          all: statsData.all || 0,
          upcoming: statsData.upcoming || 0,
          past: statsData.past || 0,
          completed: statsData.completed || 0,
          uncompleted: statsData.uncompleted || 0
        });
      } catch (error) {
        console.error('❌ [FRONTEND] Failed to load viewing stats:', error);
        setStatistics({
          all: 0,
          upcoming: 0,
          past: 0,
          completed: 0,
          uncompleted: 0
        });
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      loadDashboardData();
    }
  }, [user, loadDashboardData]);

  const handleStatusFilter = (statusId) => {
    console.log('📊 [FILTER] Status filter clicked:', statusId);
    setSelectedStatus(statusId);
    setViewingsCurrentPage(1); // Reset to first page when filtering
  };

  // Sorting functions matching Laravel pattern
  const handleSort = (key, sortConfig, setSortConfig) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Processed data with sorting, filtering and pagination for Property Viewings only
  // FIXED: Direct calculation to avoid closure issues with useMemo
  const getProcessedViewings = () => {
    console.log('📊 [FILTER] Processing viewings with selectedStatus:', selectedStatus, 'total viewings:', viewings.length);

    // Date calculations matching backend exactly
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to midnight (Laravel's setTime(0, 0, 0))
    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    oneYearAgo.setHours(0, 0, 0, 0);

    // Step 1: Apply status filter with one-year filter for past/completed/uncompleted
    let filtered = [...viewings]; // Create a copy to avoid mutation issues

    if (selectedStatus !== 999) {
      console.log('📊 [FILTER] Current date for comparison:', now.toISOString(), 'One year ago:', oneYearAgo.toISOString());

      filtered = viewings.filter(viewing => {
        // Use viewing.date (raw datetime) for date comparisons, viewing.day is formatted DD/MM/YYYY for display only
        const dateValue = viewing.date;
        if (!dateValue) {
          return false; // Exclude viewings without dates
        }

        // Parse date - handle both Date objects and ISO strings
        // Backend compares pv.date (full datetime) with Date objects at midnight
        let viewingDate;
        try {
          viewingDate = new Date(dateValue);

          // Check if date is valid
          if (isNaN(viewingDate.getTime())) {
            return false; // Invalid date
          }
        } catch (e) {
          return false; // Date parsing failed
        }
        
        // Normalize to midnight for date-only comparison (matching backend)
        // Backend sets now and oneYearAgo to midnight, so we normalize viewingDate too
        viewingDate.setHours(0, 0, 0, 0);

        switch (selectedStatus) {
          case 0: // Upcoming: date >= now (only valid dates, NO year filter)
            // Backend: pv.date >= :now
            return viewingDate >= now;
          case 1: // Past: date < now AND date >= oneYearAgo (matches backend behavior)
            // Backend: pv.date < :now AND pv.date > :oneYearAgo
            // Using >= to include records exactly on oneYearAgo boundary that backend includes due to datetime precision
            return viewingDate < now && viewingDate >= oneYearAgo;
          case 2: // Completed: attendees IS NOT NULL AND rooms_taken IS NOT NULL AND date >= oneYearAgo (matches backend)
            // Backend: pv.attendees IS NOT NULL AND pv.rooms_taken IS NOT NULL AND pv.date > :oneYearAgo
            // Using >= to include boundary records that backend includes due to datetime precision
            return viewing.attendees !== null && viewing.attendees !== undefined &&
                   viewing.rooms_taken !== null && viewing.rooms_taken !== undefined &&
                   viewingDate >= oneYearAgo;
          case 3: // Uncompleted: (attendees IS NULL OR rooms_taken IS NULL) AND date >= oneYearAgo (matches backend)
            // Backend: (pv.attendees IS NULL OR pv.rooms_taken IS NULL) AND pv.date > :oneYearAgo
            // Using >= to include boundary records that backend includes due to datetime precision
            return (viewing.attendees === null || viewing.attendees === undefined ||
                   viewing.rooms_taken === null || viewing.rooms_taken === undefined) &&
                   viewingDate >= oneYearAgo;
          default:
            return true;
        }
      });

      console.log('📊 [FILTER] After status filter:', filtered.length, 'viewings');
      console.log('📊 [FILTER] Expected count from statistics:', 
        selectedStatus === 0 ? statistics.upcoming :
        selectedStatus === 1 ? statistics.past :
        selectedStatus === 2 ? statistics.completed :
        selectedStatus === 3 ? statistics.uncompleted : 'N/A'
      );
      
      // Debug: Log sample dates for Past filter
      if (selectedStatus === 1 && filtered.length !== statistics.past) {
        console.log('⚠️ [FILTER] Count mismatch! Filtered:', filtered.length, 'Expected:', statistics.past);
        console.log('📊 [FILTER] Sample filtered dates:', filtered.slice(0, 5).map(v => ({
          id: v.id,
          day: v.day,
          date: v.date,
          parsed: v.date ? new Date(v.date).toISOString() : 'NO DATE'
        })));
        console.log('📊 [FILTER] Sample excluded dates (first 5 with dates but not matching):',
          viewings.filter(v => {
            if (!v.date) return false;
            const viewingDate = new Date(v.date);
            if (isNaN(viewingDate.getTime())) return false;
            viewingDate.setHours(0, 0, 0, 0);
            return viewingDate < now && !(viewingDate > oneYearAgo);
          }).slice(0, 5).map(v => ({
            id: v.id,
            day: v.day,
            date: v.date,
            parsed: new Date(v.date).toISOString(),
            comparison: {
              lessThanNow: new Date(v.date).setHours(0,0,0,0) < now.getTime(),
              greaterThanOneYearAgo: new Date(v.date).setHours(0,0,0,0) > oneYearAgo.getTime()
            }
          }))
        );
      }
    } else {
      console.log('📊 [FILTER] Showing all viewings (status 999)');
    }

    // Step 2: Apply search filter
    if (viewingsSearch) {
      filtered = filtered.filter(viewing =>
        (viewing.property?.name || '').toLowerCase().includes(viewingsSearch.toLowerCase()) ||
        (viewing.property?.postcode || '').toLowerCase().includes(viewingsSearch.toLowerCase()) ||
        viewing.id.toString().includes(viewingsSearch) ||
        (viewing.time_from || '').toLowerCase().includes(viewingsSearch.toLowerCase()) ||
        (viewing.time_to || '').toLowerCase().includes(viewingsSearch.toLowerCase())
      );
      console.log('📊 [FILTER] After search filter:', filtered.length, 'viewings');
    }

    // Step 3: Sort
    let sorted = filtered;
    if (viewingsSortConfig.key) {
      sorted = [...filtered].sort((a, b) => {
        let aValue = a[viewingsSortConfig.key];
        let bValue = b[viewingsSortConfig.key];

        // Handle nested properties
        if (viewingsSortConfig.key === 'viewing_property_name') {
          aValue = a.property?.name || '';
          bValue = b.property?.name || '';
        }
        if (viewingsSortConfig.key === 'postcode') {
          aValue = a.property?.postcode || '';
          bValue = b.property?.postcode || '';
        }
        if (viewingsSortConfig.key === 'day') {
          // Use raw date field for sorting (day is formatted DD/MM/YYYY for display)
          const aDateValue = a.date;
          const bDateValue = b.date;
          aValue = aDateValue ? new Date(aDateValue) : new Date(0);
          bValue = bDateValue ? new Date(bDateValue) : new Date(0);
          if (isNaN(aValue.getTime())) aValue = new Date(0);
          if (isNaN(bValue.getTime())) bValue = new Date(0);
        }
        if (viewingsSortConfig.key === 'time_from' || viewingsSortConfig.key === 'time_to') {
          const timeToMinutes = (timeStr) => {
            if (!timeStr) return 0;
            const [hours, minutes] = timeStr.split(':').map(Number);
            return (hours || 0) * 60 + (minutes || 0);
          };
          aValue = timeToMinutes(aValue);
          bValue = timeToMinutes(bValue);
        }
        if (viewingsSortConfig.key === 'id' || viewingsSortConfig.key === 'confirmed') {
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        }

        if (aValue < bValue) return viewingsSortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return viewingsSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // Step 4: Paginate
    const totalPages = Math.ceil(sorted.length / viewingsPerPage);
    const startIndex = (viewingsCurrentPage - 1) * viewingsPerPage;
    const paginated = sorted.slice(startIndex, startIndex + viewingsPerPage);

    console.log('📊 [FILTER] Final result:', paginated.length, 'of', sorted.length, 'entries');
    console.log('📊 [FILTER] Status filter:', selectedStatus, 'Filtered count:', sorted.length, 'Expected from stats:', 
      selectedStatus === 999 ? statistics.all :
      selectedStatus === 0 ? statistics.upcoming :
      selectedStatus === 1 ? statistics.past :
      selectedStatus === 2 ? statistics.completed :
      selectedStatus === 3 ? statistics.uncompleted : 'N/A'
    );

    return { data: paginated, total: sorted.length, totalPages };
  };

  // Helper function to format date for display
  const formatDayForDisplay = (dateValue) => {
    if (!dateValue) return '-';
    // If already in DD/MM/YYYY format, return as-is
    if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      return dateValue;
    }
    // Fallback: parse raw date and format in UK timezone
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Calculate on every render to ensure fresh values
  const processedViewings = getProcessedViewings();

  // Column visibility handler for Property Viewings only
  const toggleViewingsColumnVisibility = (columnKey) => {
    setViewingsColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Export functions for Property Viewings only (matching Laravel pattern)
  const handleExportViewingsCSV = async () => {
    try {
      const data = processedViewings.data.map(viewing => ({
        'Property': viewing.property?.name,
        'ID': viewing.id,
        'Postcode': viewing.property?.postcode,
        'Day': viewing.day || '-',
        'Time From': viewing.time_from || '-',
        'Time To': viewing.time_to || '-',
        'Confirmed': viewing.confirmations_count || 0
      }));

      const csvContent = 'data:text/csv;charset=utf-8,' +
        [Object.keys(data[0] || {}).join(',')]
          .concat(data.map(row => Object.values(row).join(',')))
          .join('\n');

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `property-viewings-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Viewings data exported successfully!');
    } catch (error) {
      console.error('Error exporting viewings CSV:', error);
      toast.error('Failed to export viewings data');
    }
  };

  const handleExportViewingsPDF = async () => {
    try {
      const doc = new jsPDF();

      // Add title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Property Viewings Report', 14, 15);

      // Add user info
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Sales Agent: ${user?.first_name} ${user?.last_name}`, 14, 22);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB')}`, 14, 27);

      // Add status filter info
      const statusLabels = {
        999: 'All Viewings',
        0: 'Upcoming Viewings',
        1: 'Past Viewings',
        2: 'Completed Viewings',
        3: 'Uncompleted Viewings'
      };
      doc.text(`Filter: ${statusLabels[selectedStatus] || 'All'}`, 14, 32);

      // Prepare table data from currently displayed viewings
      const tableData = processedViewings.data.map(viewing => [
        viewing.property?.name || '',
        viewing.property?.property_id || viewing.id,
        viewing.property?.postcode || '',
        viewing.day || '-',
        viewing.time_from || '-',
        viewing.time_to || '-',
        (viewing.confirmations_count || 0).toString()
      ]);

      // Add table using autoTable
      doc.autoTable({
        head: [['Property', 'ID', 'Postcode', 'Day', 'From', 'To', 'Confirmed']],
        body: tableData,
        startY: 37,
        styles: {
          fontSize: 8,
          cellPadding: 3
        },
        headStyles: {
          fillColor: [18, 39, 78], // Navy blue matching Laravel theme
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { top: 37, left: 14, right: 14 }
      });

      // Add footer with page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      const filename = `property-viewings-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);

      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting viewings PDF:', error);
      toast.error('Failed to export viewings PDF');
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      {/* Header Section */}
      <section className={styles.header} style={{ backgroundColor: '#12274e' }}>
        <div className={styles.container}>
          <div className={styles.headerContent}>
            <h2 className={styles.welcomeTitle}>
              Hello {user?.first_name} {user?.last_name}
            </h2>
            <h4 className={styles.welcomeSubtitle}>Welcome to your dashboard</h4>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.mainContent}>
        <div className={styles.container}>
          
          {/* Guardians in Process Section */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h3>Guardians in Process</h3>
            </div>
            <div className={styles.panelBody}>
              {guardiansInProcess.length > 0 ? (
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Property Name</th>
                        <th>Room</th>
                        <th className={styles.actionHead}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guardiansInProcess.map((guardian) => (
                        <tr key={guardian.id}>
                          <td>{guardian.guardian?.user?.first_name}</td>
                          <td>{guardian.guardian?.user?.last_name}</td>
                          <td>
                            <Link
                              to={`/properties/${guardian.property?.id}`}
                              className={styles.propertyLink}
                            >
                              {guardian.property?.name}
                            </Link>
                          </td>
                          <td>{guardian.room?.name}</td>
                          <td className={styles.actionCell}>
                            <Link
                              to={`/guardian-placement/step2/${guardian.property?.id}/${guardian.guardian?.id}/${guardian.room?.id}`}
                              className={styles.btnPrimary}
                            >
                              Proceed to Step 2
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.noData}>
                  <p>No guardians in process.</p>
                </div>
              )}
            </div>
          </div>

          {/* Property Viewings Status Overview */}
          <div className={styles.statusOverview}>
            <div className={styles.statusHeader}>
              <h3>Status overview</h3>
            </div>
            <div className={styles.statusBoxes}>
              <div
                className={styles.statusBox}
                onClick={() => handleStatusFilter(999)}
              >
                <p className={styles.statusValue}>{statistics.all}</p>
                <p className={styles.statusTitle}>All</p>
              </div>

              <div
                className={styles.statusBox}
                onClick={() => handleStatusFilter(0)}
              >
                <p className={styles.statusValue}>{statistics.upcoming}</p>
                <p className={styles.statusTitle}>Upcoming</p>
              </div>

              <div
                className={styles.statusBox}
                onClick={() => handleStatusFilter(1)}
              >
                <p className={styles.statusValue}>{statistics.past}</p>
                <p className={styles.statusTitle}>Past</p>
              </div>

              <div
                className={styles.statusBox}
                onClick={() => handleStatusFilter(2)}
              >
                <p className={styles.statusValue}>{statistics.completed}</p>
                <p className={styles.statusTitle}>Completed</p>
              </div>

              <div
                className={styles.statusBox}
                onClick={() => handleStatusFilter(3)}
              >
                <p className={styles.statusValue}>{statistics.uncompleted}</p>
                <p className={styles.statusTitle}>Uncompleted</p>
              </div>
            </div>
          </div>

          {/* Property Viewings Table */}
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h3>Your Viewings</h3>
            </div>
            <div className={styles.panelBody}>
              {/* Table Controls */}
              <div className={styles.tableControls}>
                <div className={styles.tableControlsLeft}>
                  <span>Show </span>
                  <select
                    value={viewingsPerPage}
                    onChange={(e) => {
                      setViewingsPerPage(Number(e.target.value));
                      setViewingsCurrentPage(1);
                    }}
                    className={styles.selectInput}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span> entries</span>
                </div>
                <div className={styles.tableControlsRight}>
                  <span>Search: </span>
                  <input
                    type="text"
                    value={viewingsSearch}
                    onChange={(e) => {
                      setViewingsSearch(e.target.value);
                      setViewingsCurrentPage(1);
                    }}
                    className={styles.searchInput}
                    placeholder="Search viewings..."
                  />
                </div>
              </div>

              {/* Export and Column Visibility Controls */}
              <div className={styles.tableActions}>
                <div className={styles.exportButtons}>
                  <button onClick={handleExportViewingsCSV} className={styles.exportBtn}>
                    Export CSV
                  </button>
                  <button onClick={handleExportViewingsPDF} className={styles.exportBtn}>
                    Export PDF
                  </button>
                </div>
                <div className={styles.columnVisibilityContainer}>
                  <button
                    onClick={() => setShowViewingsColumnVisibility(!showViewingsColumnVisibility)}
                    className={styles.columnVisibilityBtn}
                  >
                    Column Visibility
                  </button>
                  {showViewingsColumnVisibility && (
                    <div className={styles.columnVisibilityDropdown}>
                      <h4>Toggle Columns:</h4>
                      {Object.entries({
                        property: 'Property',
                        id: 'ID',
                        postcode: 'Postcode',
                        day: 'Day',
                        timeFrom: 'Time From',
                        timeTo: 'Time To',
                        confirmed: 'Confirmed',
                        actions: 'Actions'
                      }).map(([key, label]) => (
                        <label key={key} className={styles.columnToggle}>
                          <input
                            type="checkbox"
                            checked={viewingsColumnVisibility[key]}
                            onChange={() => toggleViewingsColumnVisibility(key)}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {processedViewings.data.length > 0 ? (
                <>
                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          {viewingsColumnVisibility.property && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('viewing_property_name', viewingsSortConfig, setViewingsSortConfig)}
                            >
                              Property
                              <SortIcon
                                direction={viewingsSortConfig.key === 'viewing_property_name' ? viewingsSortConfig.direction : null}
                                isActive={viewingsSortConfig.key === 'viewing_property_name'}
                              />
                            </th>
                          )}
                          {viewingsColumnVisibility.id && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('id', viewingsSortConfig, setViewingsSortConfig)}
                            >
                              ID
                              <SortIcon
                                direction={viewingsSortConfig.key === 'id' ? viewingsSortConfig.direction : null}
                                isActive={viewingsSortConfig.key === 'id'}
                              />
                            </th>
                          )}
                          {viewingsColumnVisibility.postcode && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('postcode', viewingsSortConfig, setViewingsSortConfig)}
                            >
                              Postcode
                              <SortIcon
                                direction={viewingsSortConfig.key === 'postcode' ? viewingsSortConfig.direction : null}
                                isActive={viewingsSortConfig.key === 'postcode'}
                              />
                            </th>
                          )}
                          {viewingsColumnVisibility.day && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('day', viewingsSortConfig, setViewingsSortConfig)}
                            >
                              Day
                              <SortIcon
                                direction={viewingsSortConfig.key === 'day' ? viewingsSortConfig.direction : null}
                                isActive={viewingsSortConfig.key === 'day'}
                              />
                            </th>
                          )}
                          {viewingsColumnVisibility.timeFrom && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('time_from', viewingsSortConfig, setViewingsSortConfig)}
                              title="Time From"
                            >
                              From
                              <SortIcon
                                direction={viewingsSortConfig.key === 'time_from' ? viewingsSortConfig.direction : null}
                                isActive={viewingsSortConfig.key === 'time_from'}
                              />
                            </th>
                          )}
                          {viewingsColumnVisibility.timeTo && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('time_to', viewingsSortConfig, setViewingsSortConfig)}
                              title="Time To"
                            >
                              To
                              <SortIcon
                                direction={viewingsSortConfig.key === 'time_to' ? viewingsSortConfig.direction : null}
                                isActive={viewingsSortConfig.key === 'time_to'}
                              />
                            </th>
                          )}
                          {viewingsColumnVisibility.confirmed && (
                            <th
                              className={styles.sortableHeader}
                              onClick={() => handleSort('confirmed', viewingsSortConfig, setViewingsSortConfig)}
                            >
                              Confirmed
                              <SortIcon
                                direction={viewingsSortConfig.key === 'confirmed' ? viewingsSortConfig.direction : null}
                                isActive={viewingsSortConfig.key === 'confirmed'}
                              />
                            </th>
                          )}
                          {viewingsColumnVisibility.actions && (
                            <th className={styles.actionHead}>Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {processedViewings.data.map((viewing) => (
                          <tr key={viewing.id}>
                            {viewingsColumnVisibility.property && (
                              <td>
                                <Link
                                  to={`/properties/${viewing.property?.id}`}
                                  className={styles.propertyLink}
                                >
                                  {viewing.property?.name}
                                </Link>
                              </td>
                            )}
                            {viewingsColumnVisibility.id && (
                              <td>{viewing.id}</td>
                            )}
                            {viewingsColumnVisibility.postcode && (
                              <td>{viewing.property?.postcode}</td>
                            )}
                            {viewingsColumnVisibility.day && (
                              <td>{formatDayForDisplay(viewing.day || viewing.date)}</td>
                            )}
                            {viewingsColumnVisibility.timeFrom && (
                              <td>{viewing.time_from}</td>
                            )}
                            {viewingsColumnVisibility.timeTo && (
                              <td>{viewing.time_to}</td>
                            )}
                            {viewingsColumnVisibility.confirmed && (
                              <td>
                                <Link
                                  to={`/property_viewings/${viewing.id}/edit`}
                                  className={styles.propertyLink}
                                  style={{ fontWeight: 700 }}
                                >
                                  {viewing.confirmations_count || 0}
                                </Link>
                              </td>
                            )}
                            {viewingsColumnVisibility.actions && (
                              <td className={styles.actionCell}>
                                <div className={styles.actionButtons}>
                                  <Link
                                    to={`/property_viewings/${viewing.id}/edit`}
                                    className={styles.btnSecondary}
                                  >
                                    Edit
                                  </Link>
                                  <Link
                                    to={`/property_viewings/${viewing.id}/view-details`}
                                    className={styles.btnSuccess}
                                  >
                                    View Details
                                  </Link>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className={styles.pagination}>
                    <span className={styles.paginationInfo}>
                      Showing {((viewingsCurrentPage - 1) * viewingsPerPage) + 1} to {Math.min(viewingsCurrentPage * viewingsPerPage, processedViewings.total)} of {processedViewings.total} entries
                    </span>
                    {processedViewings.totalPages > 1 && (
                      <div className={styles.paginationControls}>
                        <button
                          onClick={() => setViewingsCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={viewingsCurrentPage === 1}
                          className={styles.paginationBtn}
                        >
                          Previous
                        </button>
                        {Array.from({ length: processedViewings.totalPages }, (_, i) => i + 1)
                          .filter(page => page === 1 || page === processedViewings.totalPages || Math.abs(page - viewingsCurrentPage) <= 1)
                          .map((page, index, array) => (
                            <React.Fragment key={page}>
                              {index > 0 && array[index - 1] !== page - 1 && <span className={styles.paginationEllipsis}>...</span>}
                              <button
                                onClick={() => setViewingsCurrentPage(page)}
                                className={`${styles.paginationBtn} ${viewingsCurrentPage === page ? styles.active : ''}`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          ))}
                        <button
                          onClick={() => setViewingsCurrentPage(prev => Math.min(processedViewings.totalPages, prev + 1))}
                          disabled={viewingsCurrentPage === processedViewings.totalPages}
                          className={styles.paginationBtn}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className={styles.noData}>
                  <p>{viewingsSearch ? 'No viewings found matching search criteria.' : 'No viewings found for the selected status.'}</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default SalesWithGuardiansDashboard;
